import { useState } from "react";
import { api, formatApiErrorDetail } from "../../lib/api";
import { toast } from "sonner";
import { CheckCircle, WarningCircle } from "@phosphor-icons/react";

const PARQ_QUESTIONS = [
  { key: "heart_condition", text: "Has your doctor ever said that you have a heart condition and that you should only perform physical activity recommended by a doctor?" },
  { key: "chest_pain_activity", text: "Do you feel pain in your chest when you perform physical activity?" },
  { key: "chest_pain_rest", text: "In the past month, have you had chest pain when you were not doing physical activity?" },
  { key: "dizziness_balance", text: "Do you lose your balance because of dizziness or do you ever lose consciousness?" },
  { key: "bone_joint_problem", text: "Do you have a bone or joint problem that could be made worse by a change in your physical activity?" },
  { key: "blood_pressure_medication", text: "Is your doctor currently prescribing medication for your blood pressure or heart condition?" },
  { key: "other_reason", text: "Do you know of any other reason why you should not do physical activity?" },
];

export default function OnboardingGate({ onComplete, clientName }) {
  const [form, setForm] = useState({
    full_name: clientName || "",
    emergency_contact: "",
    emergency_phone: "",
    medical_conditions: "",
    parq_answers: {},
    agree: false,
  });
  const [submitting, setSubmitting] = useState(false);

  const setAnswer = (key, val) => setForm((f) => ({ ...f, parq_answers: { ...f.parq_answers, [key]: val } }));
  const anyFlagged = Object.values(form.parq_answers).some((v) => v === true);
  const allAnswered = PARQ_QUESTIONS.every((q) => typeof form.parq_answers[q.key] === "boolean");

  const submit = async (e) => {
    e.preventDefault();
    if (!allAnswered) {
      toast.error("Please answer every PAR-Q health screening question");
      return;
    }
    if (!form.agree) {
      toast.error("Please agree to the waiver to continue");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/portal/waiver", form);
      onComplete(data);
      toast.success("Welcome to Exist Fit!");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Something went wrong");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="onboarding-gate" className="min-h-screen bg-[#0A0A0A] px-6 py-16 flex items-center justify-center">
      <div className="max-w-2xl w-full">
        <p className="font-heading uppercase tracking-[0.2em] text-xs text-[#D4AF37] mb-2 text-center">Welcome to Exist Fit</p>
        <h1 className="font-heading text-4xl text-white tracking-tight mb-8 text-center">Let's Get You Set Up</h1>
        <div className="aspect-video rounded-2xl overflow-hidden border border-[#27272A] mb-8 bg-black">
          <video
            data-testid="onboarding-welcome-video"
            src="https://d8j0ntlcm91z4.cloudfront.net/user_38w9qb9OXikxlWiFTPfu1dDwwhq/hf_20260811_195028_bcb5e7a5-4a92-4b8e-8ed2-7720dcddaff6.mp4"
            className="w-full h-full object-contain"
            controls
            playsInline
          />
        </div>
        <form onSubmit={submit} className="bg-[#141414] border border-[#27272A] rounded-2xl p-6 sm:p-8 space-y-4" data-testid="parq-waiver-form">
          <h2 className="font-heading text-xl text-white tracking-tight mb-1">PAR-Q & Liability Waiver</h2>
          <p className="text-xs text-[#71717A] mb-4">Required before your first session for safety and legal purposes.</p>
          <input
            data-testid="waiver-name-input"
            required
            placeholder="Full Legal Name"
            value={form.full_name}
            onChange={(e) => setForm((f) => ({ ...f, full_name: e.target.value }))}
            className="w-full bg-[#0A0A0A] border border-[#27272A] rounded-xl px-4 py-3 text-white placeholder:text-[#71717A]"
          />
          <div className="grid sm:grid-cols-2 gap-4">
            <input
              data-testid="waiver-emergency-contact-input"
              required
              placeholder="Emergency Contact Name"
              value={form.emergency_contact}
              onChange={(e) => setForm((f) => ({ ...f, emergency_contact: e.target.value }))}
              className="bg-[#0A0A0A] border border-[#27272A] rounded-xl px-4 py-3 text-white placeholder:text-[#71717A]"
            />
            <input
              data-testid="waiver-emergency-phone-input"
              required
              placeholder="Emergency Contact Phone"
              value={form.emergency_phone}
              onChange={(e) => setForm((f) => ({ ...f, emergency_phone: e.target.value }))}
              className="bg-[#0A0A0A] border border-[#27272A] rounded-xl px-4 py-3 text-white placeholder:text-[#71717A]"
            />
          </div>
          <textarea
            data-testid="waiver-medical-conditions-input"
            placeholder="Any medical conditions, injuries, or medications we should know about?"
            rows={3}
            value={form.medical_conditions}
            onChange={(e) => setForm((f) => ({ ...f, medical_conditions: e.target.value }))}
            className="w-full bg-[#0A0A0A] border border-[#27272A] rounded-xl px-4 py-3 text-white placeholder:text-[#71717A]"
          />

          <div className="pt-2 space-y-3">
            <h2 className="font-heading text-xl text-white tracking-tight">PAR-Q Health Screening</h2>
            <p className="text-xs text-[#71717A] mb-2">Please answer honestly — this helps Coach Felix keep your training safe and effective.</p>
            {PARQ_QUESTIONS.map((q) => (
              <div
                key={q.key}
                data-testid={`parq-question-${q.key}`}
                className="flex items-start justify-between gap-4 bg-[#0A0A0A] border border-[#27272A] rounded-xl px-4 py-3"
              >
                <p className="text-sm text-[#D4D4D8] flex-1">{q.text}</p>
                <div className="flex gap-2 shrink-0">
                  <button
                    type="button"
                    data-testid={`parq-${q.key}-yes`}
                    onClick={() => setAnswer(q.key, true)}
                    className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${
                      form.parq_answers[q.key] === true ? "bg-[#D4AF37] text-black border-[#D4AF37]" : "border-[#27272A] text-[#A1A1AA] hover:border-[#D4AF37]/60"
                    }`}
                  >
                    Yes
                  </button>
                  <button
                    type="button"
                    data-testid={`parq-${q.key}-no`}
                    onClick={() => setAnswer(q.key, false)}
                    className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${
                      form.parq_answers[q.key] === false ? "bg-[#D4AF37] text-black border-[#D4AF37]" : "border-[#27272A] text-[#A1A1AA] hover:border-[#D4AF37]/60"
                    }`}
                  >
                    No
                  </button>
                </div>
              </div>
            ))}
            {anyFlagged && (
              <div data-testid="parq-flag-warning" className="flex items-start gap-2 bg-amber-500/10 border border-amber-500/40 rounded-xl px-4 py-3 text-sm text-amber-300">
                <WarningCircle size={20} className="shrink-0 mt-0.5" />
                <span>Based on your answers, we recommend consulting your physician before starting. Coach Felix will follow up to discuss safe modifications.</span>
              </div>
            )}
          </div>

          <label className="flex items-start gap-3 text-sm text-[#A1A1AA] pt-2">
            <input
              data-testid="waiver-agree-checkbox"
              type="checkbox"
              checked={form.agree}
              onChange={(e) => setForm((f) => ({ ...f, agree: e.target.checked }))}
              className="mt-1 accent-[#D4AF37]"
            />
            I confirm the above is accurate and voluntarily assume all risks associated with physical training with Exist Fit. Typing my name above serves as my digital signature.
          </label>
          <button
            data-testid="waiver-submit-button"
            disabled={submitting}
            type="submit"
            className="w-full flex items-center justify-center gap-2 bg-[#D4AF37] disabled:opacity-50 text-black font-semibold rounded-full py-3.5 hover:bg-white transition-colors"
          >
            <CheckCircle size={18} /> {submitting ? "Submitting..." : "Sign & Enter Portal"}
          </button>
        </form>
      </div>
    </div>
  );
}
